import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useContact } from '../contexts/ContactContext';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const FAQ = () => {
  const { contactInfo } = useContact();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  
  const [activeFaq, setActiveFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await supabase.from('requests').insert([{
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        type: 'FAQ Inquiry',
        message: formData.message
      }]);
      
      if (error) throw error;
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error submitting FAQ request:', err);
      alert('Failed to submit question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const { data, error } = await supabase.from('faqs').select('*').order('created_at', { ascending: false });
        if (error) throw error;
        const fetchedFaqs = data || [];
        setFaqs(fetchedFaqs);
        
        // Extract unique categories
        const uniqueCategories = ['All'];
        fetchedFaqs.forEach(faq => {
          if (faq.category && !uniqueCategories.includes(faq.category)) {
            uniqueCategories.push(faq.category);
          }
        });
        setCategories(uniqueCategories);
      } catch (err) {
        console.error('Error fetching FAQs:', err);
      } finally {
        setLoadingFaqs(false);
      }
    };
    fetchFaqs();
  }, []);

  const reasons = [
    'Trusted by 5000+ Happy Clients',
    '10+ Years of Real Estate Excellence',
    '2000+ Crore Worth of Properties Delivered',
    'Transparent Deals & Expert Guidance',
    'End-to-End Support - Always!'
  ];

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Frequently Asked Questions | HBB0011 REALTY"
        description="Find answers to common questions about our luxury real estate projects, investment opportunities, and property management services."
        keywords="HBB0011 REALTY FAQ, real estate questions, investment inquiries, property management FAQs"
      />
      
      {/* 1. Hero Section (Founder photo removed as requested) */}
      <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 lg:min-h-[50vh] flex items-center overflow-hidden border-b border-gray-800">
        {/* Background Image Blend */}
        <div className="absolute inset-0 z-0 flex justify-end">
          <div className="w-full lg:w-3/4 h-full relative">
            <img 
              src={buildingImg} 
              alt="Luxury real estate background" 
              className="w-full h-full object-cover opacity-20 lg:opacity-40 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 lg:via-[#041424]/40 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-transparent to-transparent"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-center lg:text-left">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
              <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
              <span className="text-[#D4AF37] uppercase">FAQ</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-light text-white mb-4 tracking-tight uppercase">
              FAQS
            </h1>
            
            <h2 className="text-xl sm:text-2xl md:text-3xl text-[#D4AF37] font-medium mb-6 uppercase tracking-wide leading-tight">
              FIND ANSWERS TO <br className="hidden sm:block"/>
              YOUR QUESTIONS.
            </h2>
            
            <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-md mx-auto lg:mx-0">
              Here are some of the most common questions about our services, projects, and processes. Can't find what you're looking for? Contact us!
            </p>
          </div>
        </div>
      </section>

      {/* 2. Main Content Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Left Column: FAQs */}
          <div className="lg:col-span-7 xl:col-span-8 flex flex-col gap-8">
            <h3 className="text-xl sm:text-2xl text-white font-light uppercase tracking-widest border-b border-gray-800 pb-4">
              Frequently Asked Questions
            </h3>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-3">
              {categories.map((category, index) => (
                <button 
                  key={index}
                  onClick={() => setActiveCategory(category)}
                  className={`px-5 py-2.5 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest transition-colors ${
                    activeCategory === category 
                      ? 'bg-[#D4AF37] text-[#041424]' 
                      : 'border border-gray-700 text-gray-400 hover:border-[#D4AF37] hover:text-[#D4AF37]'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* FAQ Accordion */}
            {loadingFaqs ? (
               <div className="flex justify-center py-10"><span className="material-symbols-outlined animate-spin text-4xl text-[#D4AF37]">sync</span></div>
            ) : (
              <div className="space-y-4">
                {(activeCategory === 'All' ? faqs : faqs.filter(f => f.category === activeCategory)).map((faq, index) => (
                  <div key={faq.id || index} className="border border-gray-800 rounded-sm overflow-hidden">
                  <button 
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className={`w-full flex items-center justify-between p-5 sm:p-6 transition-colors ${
                      activeFaq === index ? 'bg-[#06121e]' : 'bg-[#041424] hover:bg-[#06121e]'
                    }`}
                  >
                    <span className="text-left text-sm sm:text-base font-medium text-white pr-4">
                      {index + 1}. {faq.question}
                    </span>
                    <span className={`material-symbols-outlined shrink-0 transition-transform duration-300 ${
                      activeFaq === index ? 'text-[#D4AF37]' : 'text-gray-500'
                    }`}>
                      {activeFaq === index ? 'remove' : 'add'}
                    </span>
                  </button>
                  
                  {/* Expandable Answer */}
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    activeFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="p-5 sm:p-6 pt-0 bg-[#06121e] text-gray-400 text-xs sm:text-sm leading-relaxed border-t border-gray-800/50 mt-2">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>

          {/* Right Column: Sidebar */}
          <aside className="lg:col-span-5 xl:col-span-4 flex flex-col gap-8">
            
            {/* Form Box */}
            <div className="bg-[#06121e] border border-gray-800 rounded-sm p-6 sm:p-8">
              <h4 className="text-white text-lg font-bold uppercase tracking-widest mb-2">Still Have Questions?</h4>
              <p className="text-gray-400 text-xs sm:text-sm mb-6 leading-relaxed">Our team is here to help you with any queries you may have.</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {success && (
                  <div className="bg-green-500/20 border border-green-500/50 text-green-400 p-3 rounded-sm text-xs">
                    Your question has been submitted! We'll reply soon.
                  </div>
                )}
                <div className="relative">
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Full Name" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-3 text-white text-xs focus:border-[#D4AF37] focus:outline-none transition-colors" />
                  <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg pointer-events-none">person</span>
                </div>
                
                <div className="relative">
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="Email Address" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-3 text-white text-xs focus:border-[#D4AF37] focus:outline-none transition-colors" />
                  <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg pointer-events-none">mail</span>
                </div>
                
                <div className="relative">
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="Phone Number" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-3 text-white text-xs focus:border-[#D4AF37] focus:outline-none transition-colors" />
                  <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg pointer-events-none">call</span>
                </div>
                
                <div className="relative">
                  <textarea rows="4" name="message" value={formData.message} onChange={handleInputChange} required placeholder="Your Question / Message" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-3 text-white text-xs focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"></textarea>
                </div>
                
                <button type="submit" disabled={loading} className={`w-full font-bold text-[10px] sm:text-xs uppercase tracking-widest py-4 rounded-sm transition-colors flex items-center justify-center gap-2 mt-2 ${loading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-[#D4AF37] text-[#041424] hover:bg-white'}`}>
                  {loading ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-sm">sync</span> Processing...
                    </>
                  ) : (
                    <>
                      Submit Your Question <span className="material-symbols-outlined text-sm">arrow_forward</span>
                    </>
                  )}
                </button>
              </form>

              <div className="flex items-center justify-center gap-2 mt-4 text-gray-400 text-[10px]">
                <span className="material-symbols-outlined text-[#D4AF37] text-sm">shield</span>
                We respect your privacy. Your information is safe with us.
              </div>
            </div>

            {/* Why Choose Us Box */}
            <div className="bg-[#06121e] border border-gray-800 rounded-sm p-6 sm:p-8">
              <h4 className="text-white text-lg font-bold uppercase tracking-widest mb-6">Why Choose HBB0011?</h4>
              <ul className="space-y-4">
                {reasons.map((reason, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-[#D4AF37] text-xl shrink-0">check_circle</span>
                    <span className="text-gray-300 text-xs sm:text-sm">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Box */}
            <div className="relative rounded-sm overflow-hidden border border-gray-800 h-64 p-6 sm:p-8 flex flex-col justify-end group">
              <img src={buildingImg} alt="Luxury Interior" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-[#041424]/80 to-transparent"></div>
              
              <div className="relative z-10">
                <h4 className="text-white text-lg font-bold uppercase tracking-widest mb-2">Ready to Get Started?</h4>
                <p className="text-gray-400 text-xs mb-4">Talk to our real estate experts today and take the first step towards your dream property.</p>
                <button className="bg-[#D4AF37] text-[#041424] font-bold text-[10px] sm:text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-white transition-colors flex items-center justify-center gap-2 w-fit">
                  Talk to an Expert <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </button>
              </div>
            </div>

          </aside>

        </div>
      </main>

      {/* 3. Pre-Footer Features Strip */}
      <section className="border-t border-b border-gray-800 bg-[#06121e] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 divide-y sm:divide-y-0 sm:divide-x divide-gray-800">
          
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">headset_mic</span>
            <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-2">Need Help?</h4>
            <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Talk to our experts.<br/>Call us now</p>
            <a href={`tel:${contactInfo?.phone?.replace(/[^0-9+]/g, '')}`} className="text-[#D4AF37] text-[10px] hover:underline">{contactInfo?.phone}</a>
          </div>

          <div className="flex flex-col items-center text-center lg:items-start lg:text-left pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">calendar_month</span>
            <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-2">Book a Site Visit</h4>
            <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Visit our projects and experience the quality.</p>
            <Link to="/book-visit" className="text-[#D4AF37] text-[10px] hover:underline">Book your visit today</Link>
          </div>

          <div className="flex flex-col items-center text-center lg:items-start lg:text-left pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">download</span>
            <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-2">Download Brochure</h4>
            <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Download our project brochures and company profile.</p>
            <Link to="/brochure" className="text-[#D4AF37] text-[10px] hover:underline">Get detailed information</Link>
          </div>

          <div className="flex flex-col items-center text-center lg:items-start lg:text-left pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">mail</span>
            <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-2">Email Us</h4>
            <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Drop us an email anytime.</p>
            <a href={`mailto:${contactInfo?.email}`} className="text-[#D4AF37] text-[10px] hover:underline">{contactInfo?.email}</a>
          </div>

        </div>
      </section>

    </div>
  );
};

export default FAQ;